/**
 * Skin preference — the player's chosen theme override, persisted in localStorage,
 * plus the level↔skin coupling rule. Kept tiny + pure so it is unit-testable and
 * has no Pixi / DOM dependency (effectiveTheme / effectivePalette are the contracts
 * the picker + customizer + main use).
 *
 * Two prefs coexist:
 *  - LEGACY `maker_skin_pref` (Brief 003): a single whole-theme override string.
 *  - `maker_skin_palette`: a per-category mix `{player,enemies,tiles,goal,background,
 *    objects → theme}` (the personal palette). An absent category renders the level's
 *    authored theme. The legacy pref is migrated into a uniform (all-6) palette on read.
 */
import type { LevelFormat } from './LevelFormat';
import { SKIN_CATEGORIES, type Palette, type SkinCategory } from '../assets/AssetManifest';

/** localStorage key for the LEGACY single-theme pref (still read for migration). */
export const SKIN_PREF_KEY = 'maker_skin_pref';
/** localStorage key for the per-category personal palette (JSON). */
export const SKIN_PALETTE_KEY = 'maker_skin_palette';

/** Read the saved skin pref, or null if unset / storage unavailable. */
export function getSkinPref(): string | null {
  try {
    return localStorage.getItem(SKIN_PREF_KEY);
  } catch {
    return null; // storage blocked (private mode / sandbox) → behave as "no pref"
  }
}

/** Persist (theme) or clear (null) the player's skin pref. Storage errors are swallowed. */
export function setSkinPref(theme: string | null): void {
  try {
    if (theme === null) localStorage.removeItem(SKIN_PREF_KEY);
    else localStorage.setItem(SKIN_PREF_KEY, theme);
  } catch {
    /* storage disabled — the pref just won't persist this session */
  }
}

/**
 * Resolve the theme actually rendered for a level:
 *   effective = (level allows override && a pref is set) ? pref : level.theme
 * Per Brief 003 / LevelFormat: an ABSENT `allowSkinOverride` means override is
 * ALLOWED (skins default-on); only an explicit `false` pins the authored theme.
 */
export function effectiveTheme(level: LevelFormat, pref: string | null): string {
  if (level.meta.allowSkinOverride !== false && pref) return pref;
  return level.theme;
}

// ── Per-category palette ──────────────────────────────────────────────────────

/** A palette with every category pinned to one theme (a whole-theme "quick swap"
 *  expressed in the palette model — used by the in-game K picker + legacy migration). */
export function fillPalette(theme: string): Palette {
  const p: Palette = {};
  for (const c of SKIN_CATEGORIES) p[c] = theme;
  return p;
}

/** If every category is set to the SAME theme, return it (a uniform whole-theme
 *  palette); otherwise null (a mixed palette, or empty). */
export function uniformTheme(palette: Palette): string | null {
  const first = palette[SKIN_CATEGORIES[0]];
  if (!first) return null;
  return SKIN_CATEGORIES.every((c) => palette[c] === first) ? first : null;
}

/** Drop unknown categories + non-string values from a parsed blob (defensive against
 *  hand-edited / stale storage). Unknown THEME names are intentionally kept — they
 *  resolve through composeManifest's fallback chain rather than being second-guessed here. */
export function sanitizePalette(raw: unknown): Palette {
  const out: Palette = {};
  if (!raw || typeof raw !== 'object') return out;
  const obj = raw as Record<string, unknown>;
  for (const c of SKIN_CATEGORIES) {
    const v = obj[c];
    if (typeof v === 'string' && v) out[c] = v;
  }
  return out;
}

/**
 * Read the personal palette. Precedence:
 *  1. `maker_skin_palette` (JSON) if present,
 *  2. else migrate the LEGACY `maker_skin_pref` whole-theme string → a uniform palette,
 *  3. else the empty palette (render each level's authored theme).
 * Never throws (storage blocked / corrupt JSON → empty palette).
 */
export function getPalettePref(): Palette {
  try {
    const rawPalette = localStorage.getItem(SKIN_PALETTE_KEY);
    if (rawPalette) return sanitizePalette(JSON.parse(rawPalette));
    const legacy = localStorage.getItem(SKIN_PREF_KEY);
    if (legacy) {
      const migrated = fillPalette(legacy);
      setPalettePref(migrated); // persist forward so the migration runs once
      return migrated;
    }
  } catch {
    /* storage blocked / bad JSON → behave as "no palette" */
  }
  return {};
}

/** Persist the palette (empty → clear it). Also clears the legacy key so the two
 *  never disagree. Storage errors are swallowed. */
export function setPalettePref(palette: Palette): void {
  try {
    const clean = sanitizePalette(palette);
    if (Object.keys(clean).length === 0) localStorage.removeItem(SKIN_PALETTE_KEY);
    else localStorage.setItem(SKIN_PALETTE_KEY, JSON.stringify(clean));
    localStorage.removeItem(SKIN_PREF_KEY); // supersede the Brief 003 single-theme pref
  } catch {
    /* storage disabled — the palette just won't persist this session */
  }
}

/**
 * The palette actually applied when rendering `level`: the personal palette, UNLESS
 * the level pins its own skin (`meta.allowSkinOverride === false`), in which case no
 * override applies (empty palette → the authored theme for every category). The
 * compose BASE is always the level's authored theme.
 */
export function effectivePalette(level: LevelFormat, palette: Palette | null): Palette {
  if (level.meta.allowSkinOverride === false || !palette) return {};
  return sanitizePalette(palette);
}

/** Set (or clear) one category of the palette, returning the new palette (pure). */
export function withCategory(palette: Palette, category: SkinCategory, theme: string | null): Palette {
  const next: Palette = { ...palette };
  if (theme === null) delete next[category];
  else next[category] = theme;
  return next;
}
